import React, { useState, useEffect, useContext, useMemo } from 'react';
import './NavBar.css';
import IconButton from '@mui/material/IconButton';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { Tooltip } from '@mui/material';

import { MapContext } from '../MapContext';
import { LayerContext } from '../LayerContext';
import proj4 from 'proj4';
import { generateClient } from 'aws-amplify/api';
import { createBookmark } from '../../graphql/mutations';
import { useUser } from '../UserContext';
import {
  extractCircleRadiusFromLayerPaintProperty,
  extractCircleOpacityFromLayerPaintProperty,
  extractSgeTypeFilterFromLayerProperty,
  extractColormapFromFictureLayer
} from '../../common/layer/util';

const client = generateClient();

// Setting up the projections for conversion
const fromProjection = 'EPSG:4326'; // WGS84
const toProjection = 'EPSG:3857'; // Web Mercator
// opacity property name for layer asset types
const opacityProperty = {
  'gene': 'circle-opacity',
  'gene_group': 'circle-opacity',
  'hexa': 'circle-opacity',
  'bg': 'raster-opacity',
  'fic': 'raster-opacity',
  'roi': 'fill-opacity',
}

const ViewState = ({ layerProps }) => {
  const { map } = useContext(MapContext);
  const { user } = useUser();
  const { layers, dispatch } = useContext(LayerContext);
  const [center, setCenter] = useState({ lng: null, lat: null });
  const [zoom, setZoom] = useState(null);

  const convertedCoords = useMemo(() => {
    if (center.lng !== null && center.lat !== null) {
      return proj4(proj4.defs(fromProjection), proj4.defs(toProjection), [center.lng, center.lat]);
    }
    return [null, null];
  }, [center]);

  useEffect(() => {
    const updateViewState = () => {
      if (map && map.current) {
        const newCenter = map.current.getCenter();
        const newZoom = map.current.getZoom().toFixed(2);
        setCenter(newCenter);
        setZoom(newZoom);
      }
    };

    if (map && map.current) {
      updateViewState();
      const initialCenter = map.current.getCenter();
      setCenter(initialCenter);

      map.current.on('move', updateViewState);
      map.current.on('zoom', updateViewState);
    }
  }, [map]);

  const handleBookmarkClick = async () => {
    try {
      const result = await client.graphql({
        query: createBookmark,
        variables: {
          input: {
            description: '',
            viewState: {
              center: [center.lng, center.lat],
              zoom: zoom
            },
            layers: layers.filter(l => l.assetType !== 'roi').map((l) => {
              const layerId = l.id;
              const layerTitle = layerProps.find(props => props.layerId === layerId)?.layerTitle || null;
              const assetType = l.assetType;
              const assetUrl = l.assetUrl;
              const subLayers = assetType === "gene_group" ? l.title.map((title, idx) => ({
                gene: title,
                assetUrl: l.assetUrl[idx],
                visibility: map.current.getLayoutProperty(l.subLayerIds[idx], 'visibility') === "visible" ? true : false
              })) : null;
              const rasterOpacity = assetType === "bg" || assetType === "fic" ? map.current.getPaintProperty(l.id, opacityProperty[l.assetType]) : null;
              const circleColor = assetType === "gene" ? map.current.getPaintProperty(l.id, 'circle-color') : assetType === "gene_group" ? map.current.getPaintProperty(l.subLayerIds[0], 'circle-color') : null;
              const circleRadius = assetType === "gene" || assetType === "hexa" ? extractCircleRadiusFromLayerPaintProperty(map.current.getPaintProperty(l.id, 'circle-radius')) : assetType === "gene_group" ? extractCircleRadiusFromLayerPaintProperty(map.current.getPaintProperty(l.subLayerIds[0], 'circle-radius')) : null;
              const circleOpacity = assetType === "gene" || assetType === "hexa" ? extractCircleOpacityFromLayerPaintProperty(map.current.getPaintProperty(l.id, opacityProperty[l.assetType])) : assetType === "gene_group" ? extractCircleOpacityFromLayerPaintProperty(map.current.getPaintProperty(l.subLayerIds[0], opacityProperty[l.assetType])) : null;
              const sgeCheckbox = assetType === "gene" ? extractSgeTypeFilterFromLayerProperty(map.current.getFilter(l.id)) : assetType === "gene_group" ? extractSgeTypeFilterFromLayerProperty(map.current.getFilter(l.subLayerIds[0])) : null;
              const fillOpacity = assetType === "roi" ? map.current.getPaintProperty(l.id, opacityProperty[l.assetType]) : null;
              const colormap = assetType === "hexa" ? JSON.stringify(map.current.getPaintProperty(l.id, "circle-color")) : assetType === "fic" ? extractColormapFromFictureLayer(map, layerId) : null;
              const visibility = assetType === "gene_group" ? true : map.current.getLayoutProperty(l.id, 'visibility') === "visible" ? true : false;
              const styles = {
                layerId: layerId,
                layerTitle: layerTitle,
                assetType: assetType,
                assetUrl: assetUrl,
                subLayers: subLayers,
                rasterOpacity: rasterOpacity,
                circleColor: circleColor,
                circleRadius: circleRadius,
                circleOpacity: circleOpacity,
                sgeCheckbox: sgeCheckbox,
                fillOpacity: fillOpacity,
                colormap: colormap,
                visibility: visibility
              };
              return styles;
            })
          }
        },
        authMode: 'userPool'
      });
      const bookmarkId = result.data.createBookmark.id;

      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('bookmarkId', bookmarkId);

      // Use the History API to update the URL without reloading the page
      window.history.pushState({}, '', currentUrl);

      await navigator.clipboard.writeText(window.location.href);
      console.log('The bookmark URL has been copied to the clipboard.');
    } catch (err) {
      console.error('Failed to copy URL to clipboard', err);
    }
  };

  return (
    <div className="view-state-container">
      <div className="view-state-item">
        <div className="view-state-coordinates">
          <div>X: {center?.lng?.toFixed(4)} / {convertedCoords[0]?.toFixed(2)}</div>
          <div>Y: {center?.lat?.toFixed(4)} / {convertedCoords[1]?.toFixed(2)}</div>
        </div>
      </div>
      <div className="view-state-item">
        <div className="view-state-zoom">
          <div>Zoom</div>
          <div>{zoom}</div>
        </div>
      </div>
      {user ? <Tooltip title={"Copy Bookmark URL to the clipboard."} placement="bottom-start">
        <IconButton onClick={handleBookmarkClick} color="secondary" aria-label="bookmark">
          <BookmarkAddIcon />
        </IconButton>
      </Tooltip> : <></>}
    </div>
  );
}

export default ViewState;

